import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';

import Text from 'v3/components/Text';
import Button from 'v3/components/Button';
import Space from 'v3/components/Space';
import SwitchOption from 'v3/components/SwitchOption';
import Select from 'v3/components/Select';
import Input from 'v3/components/Input';
import Modal from 'v3/components/Modal';
import ApplicationLayout from 'v3/layouts/Application';
import { formatDate } from 'v3/utils/formatter';

import Section from '../../shared/Section';
import { useNegotiations } from '../../context';

export function Details() {
  const {
    negotiation,
    editData,
    setEditData,
    loading,
    updateNegotiation,
    activateNegotiation,
    deactivateNegotiation,
    confirmDeactivateModal,
    seedEditData,
    automationOptions,
    occurrenceTypeOptions,
  } = useNegotiations();

  const [isEditing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [occurrenceTypesError, setOccurrenceTypesError] = useState('');
  const [autoRegistrationValidationDaysError, setAutoRegistrationValidationDaysError] = useState('');
  const [autoGroValidationDaysError, setAutoGroValidationDaysError] = useState('');

  function validateRequiredSelection(value) {
    return value?.length ? '' : 'Selecione ao menos um tipo de ocorrência';
  }

  if (!loading && !negotiation) {
    return null;
  }

  function formatDaysLabel(days) {
    return `${days} ${days === 1 ? 'dia' : 'dias'}`;
  }

  function validateRequiredDays(rawValue) {
    if (rawValue === null || rawValue === undefined || rawValue === '') {
      return 'A quantidade de dias é obrigatória';
    }
    const days = Number(rawValue);
    if (!Number.isInteger(days) || days <= 0) {
      return 'A quantidade de dias deve ser um número inteiro maior que 0';
    }
    return '';
  }

  async function handleSave() {
    if (editData.autoCreateOccurrence) {
      const error = validateRequiredSelection(editData.occurrenceTypes);
      if (error) {
        setOccurrenceTypesError(error);
        return;
      }
    }
    
    const registrationError = editData.autoRegistrationValidation
      ? validateRequiredDays(editData.autoRegistrationValidationDays)
      : '';
    const groError = editData.autoGroValidation
      ? validateRequiredDays(editData.autoGroValidationDays)
      : '';

    setAutoRegistrationValidationDaysError(registrationError);
    setAutoGroValidationDaysError(groError);

    if (registrationError || groError) {
      return;
    }

    setSaving(true);
    try {
      await updateNegotiation();
      setEditing(false);
    } catch {
      // updateNegotiation already shows snackbar; stay in editing mode
    } finally {
      setSaving(false);
    }
  }

  function handleCancel() {
    seedEditData(negotiation);
    setOccurrenceTypesError('');
    setAutoRegistrationValidationDaysError('');
    setAutoGroValidationDaysError('');
    setEditing(false);
  }

  function handleToggleField(field) {
    setEditData(prev => ({ ...prev, [field]: !prev[field] }));
  }

  function handleToggleAutoCreateOccurrence() {
    setEditData(prev => {
      const next = !prev.autoCreateOccurrence;
      if (!next) {
        setOccurrenceTypesError('');
        return { ...prev, autoCreateOccurrence: next, occurrenceTypes: [] };
      }
      setOccurrenceTypesError(validateRequiredSelection(prev.occurrenceTypes));
      return { ...prev, autoCreateOccurrence: next };
    });
  }

  function handleChangeOccurrenceTypes(selected) {
    const value = selected || [];
    setEditData(prev => ({ ...prev, occurrenceTypes: value }));
    setOccurrenceTypesError(validateRequiredSelection(value));
  }

  const headerRight = isEditing ? (
    <Space x="5px">
      <Button variant="secondary" onClick={handleCancel} className="py-2">
        <Text type="regular" color="dark" weight="500">
          Cancelar
        </Text>
      </Button>
      <Button
        variant="primary"
        onClick={handleSave}
        loading={saving}
        className="py-2"
      >
        <Text type="regular" color="white" weight="500">
          Salvar
        </Text>
      </Button>
    </Space>
  ) : (
    <Button
      variant="secondary"
      onClick={() => setEditing(true)}
      className="py-2"
    >
      <Text color="dark" weight="500" type="regular">
        Editar
      </Text>
    </Button>
  );

  return (
    <ApplicationLayout
      title="Detalhes da Negociação"
      RightComponent={
        <Link to="/negociacoes-carga">
          <Button variant="secondary">
            <Text color="dark" weight={500} type="regular">
              Voltar
            </Text>
          </Button>
        </Link>
      }
    >
      <Section
        Title="Dados gerais"
        HeaderRightComponent={headerRight}
        loading={loading}
      >
        <Row>
          <Col xs={12} md={12} className="mb-3">
            <SwitchOption
              value={Boolean(negotiation.active)}
              onClick={() => {
                negotiation.active
                  ? confirmDeactivateModal.open()
                  : activateNegotiation()
              }
              }
            />
          </Col>

          <Col xs={12} md={6}>
            <Text type="label" color="dark">
              Nome
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {negotiation?.name || 'Não informado'}
            </Text>
          </Col>

          <Col xs={12} md={6}>
            <Text type="label" color="dark">
              Número
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {negotiation?.negotiationNumber || 'Não informado'}
            </Text>
          </Col>

          <Col xs={12} md={6}>
            <Text type="label" color="dark">
              Centro de Custo
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {negotiation?.costCenter?.name || 'Não informado'}
            </Text>
          </Col>

          <Col xs={12} md={6}>
            <Text type="label" color="dark">
              Id externo
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {negotiation?.negotiationExternalId || 'Não informado'}
            </Text>
          </Col>

          <Col xs={12} md={6}>
            <Text type="label" color="dark">
              Tomador
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {negotiation?.person?.socialName || negotiation?.person?.label || 'Não informado'}
            </Text>
          </Col>

          <Col xs={12} md={6}>
            <Text type="label" color="dark">
              Vigência inicial
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {negotiation?.validityInitial
                ? formatDate(negotiation.validityInitial)
                : 'Não informado'}
            </Text>
          </Col>

          <Col xs={12} md={6}>
            <Text type="label" color="dark">
              Vigência final
            </Text>
            <Text type="regular" color="gray" as="p" className="mt-2">
              {negotiation?.validityFinal
                ? formatDate(negotiation.validityFinal)
                : 'Não informado'}
            </Text>
          </Col>

          <Col xs={12} className="mt-3 mb-2">
            <Text type="header" color="dark">
              Configurações de negociação
            </Text>
          </Col>

          {isEditing ? (
            <>
              <Col xs={12} md={6} className="mb-3">
                <Text type="label" color="dark" className="mb-2">
                  Validação automático cadastro
                </Text>
                <SwitchOption
                  value={editData.autoRegistrationValidation}
                  onClick={() => {
                    const enabling = !editData.autoRegistrationValidation;
                    if (enabling) {
                      setAutoRegistrationValidationDaysError(
                        validateRequiredDays(editData.autoRegistrationValidationDays),
                      );
                    } else {
                      setAutoRegistrationValidationDaysError('');
                      setEditData(prev => ({ ...prev, autoRegistrationValidationDays: null }));
                    }
                    handleToggleField('autoRegistrationValidation');
                  }}
                />

                {editData.autoRegistrationValidation && (
                  <Col xs={6} md={6}>
                    <Input
                      label="Validade da regra (dias) *"
                      type="number"
                      min={1}
                      step={1}
                      className="mt-2"
                      value={editData.autoRegistrationValidationDays ?? ''}
                      error={autoRegistrationValidationDaysError}
                      onChange={e => {
                        const { value } = e.target;
                        setAutoRegistrationValidationDaysError(validateRequiredDays(value));
                        setEditData(prev => ({
                          ...prev,
                          autoRegistrationValidationDays: value === '' ? null : Number(value),
                        }));
                      }}
                      placeholder="Quantidade de dias"
                    />
                  </Col>
                )}
              </Col>

              <Col xs={12} md={6} className="mb-3">
                <Text type="label" color="dark" className="mb-2">
                  Validação automático GRO
                </Text>
                <SwitchOption
                  value={editData.autoGroValidation}
                  onClick={() => {
                    const enabling = !editData.autoGroValidation;
                    if (enabling) {
                      setAutoGroValidationDaysError(
                        validateRequiredDays(editData.autoGroValidationDays),
                      );
                    } else {
                      setAutoGroValidationDaysError('');
                      setEditData(prev => ({ ...prev, autoGroValidationDays: null }));
                    }
                    handleToggleField('autoGroValidation');
                  }}
                />

                {editData.autoGroValidation && (
                  <Col xs={6} md={6}>
                    <Input
                      label="Validade da regra (dias) *"
                      type="number"
                      min={1}
                      step={1}
                      className="mt-2"
                      value={editData.autoGroValidationDays ?? ''}
                      error={autoGroValidationDaysError}
                      onChange={e => {
                        const { value } = e.target;
                        setAutoGroValidationDaysError(validateRequiredDays(value));
                        setEditData(prev => ({
                          ...prev,
                          autoGroValidationDays: value === '' ? null : Number(value),
                        }));
                      }}
                      placeholder="Quantidade de dias"
                    />
                  </Col>
                )}
              </Col>

              <Col xs={12} md={6} className="mb-3">
                <Text type="label" color="dark" className="mb-2">
                  Informar chegada automática por raio
                </Text>
                <SwitchOption
                  value={editData.autoArrivalByRadius}
                  onClick={() => handleToggleField('autoArrivalByRadius')}
                />
              </Col>

              <Col xs={12} md={6} className="mb-3">
                <Text type="label" color="dark" className="mb-2">
                  Permite protocolar documentos da viagem
                </Text>
                <SwitchOption
                  value={editData.protocolDocs}
                  onClick={() => handleToggleField('protocolDocs')}
                />
              </Col>

              <Col xs={12} md={6} className="mb-3">
                <Text type="label" color="dark" className="mb-2">
                  Criar ocorrência automática
                </Text>
                <SwitchOption
                  value={editData.autoCreateOccurrence}
                  onClick={handleToggleAutoCreateOccurrence}
                />

                {editData.autoCreateOccurrence && (
                  <Select
                    label="Tipos de ocorrência"
                    multiple
                    value={editData.occurrenceTypes}
                    options={occurrenceTypeOptions}
                    getOptionLabel={option => option.name}
                    getOptionValue={option => option.id}
                    onChange={handleChangeOccurrenceTypes}
                    placeholder="Tipos de ocorrência"
                    error={occurrenceTypesError}
                  />
                )}
              </Col>

              <Col xs={12} md={6} className="mb-3">
                <Text type="label" color="dark" className="mb-2">
                  Gerar comprovante de entrega
                </Text>
                <SwitchOption
                  value={editData.generateDeliveryProof}
                  onClick={() => handleToggleField('generateDeliveryProof')}
                />
              </Col>

              <Col xs={12} md={6} className="mb-3">
                <Select
                  label="Integrações"
                  multiple
                  value={editData.integracoes}
                  options={automationOptions}
                  getOptionLabel={option => option.name}
                  getOptionValue={option => option.id}
                  onChange={selected =>
                    setEditData(prev => ({
                      ...prev,
                      integracoes: selected || [],
                    }))
                  }
                  placeholder="Selecione integrações"
                />
              </Col>
            </>
          ) : (
            <>
              <Col xs={12} md={6}>
                <Text type="label" color="dark">
                  Validação automático cadastro
                </Text>
                <Text type="regular" color="gray" as="p" className="mt-2">
                  {negotiation?.autoRegistrationValidation
                    ? `Sim (${formatDaysLabel(negotiation.autoRegistrationValidationDays)})`
                    : 'Não'}
                </Text>
              </Col>

              <Col xs={12} md={6}>
                <Text type="label" color="dark">
                  Validação automático GRO
                </Text>
                <Text type="regular" color="gray" as="p" className="mt-2">
                  {negotiation?.autoGroValidation
                    ? `Sim (${formatDaysLabel(negotiation.autoGroValidationDays)})`
                    : 'Não'}
                </Text>
              </Col>

              <Col xs={12} md={6}>
                <Text type="label" color="dark">
                  Informar chegada automática por raio
                </Text>
                <Text type="regular" color="gray" as="p" className="mt-2">
                  {negotiation?.autoArrivalByRadius ? 'Sim' : 'Não'}
                </Text>
              </Col>
              <Col xs={12} md={6}>
                <Text type="label" color="dark">
                  Permite protocolar documentos da viagem
                </Text>
                <Text type="regular" color="gray" as="p" className="mt-2">
                  {negotiation?.protocolDocs ? 'Sim' : 'Não'}
                </Text>
              </Col>

              <Col xs={12} md={6}>
                <Text type="label" color="dark">
                  Criar ocorrência automática
                </Text>
                <Text type="regular" color="gray" as="p" className="mt-2">
                  {negotiation?.autoCreateOccurrence ? 'Sim' : 'Não'}
                </Text>
              </Col>
              <Col xs={12} md={6}>
                <Text type="label" color="dark">
                  Gerar comprovante de entrega
                </Text>
                <Text type="regular" color="gray" as="p" className="mt-2">
                  {negotiation?.generateDeliveryProof ? 'Sim' : 'Não'}
                </Text>
              </Col>

              {negotiation?.autoCreateOccurrence && (
                <Col xs={12} md={6}>
                  <Text type="label" color="dark">
                    Tipos de ocorrência automática
                  </Text>
                  <div
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '0.5rem',
                      marginTop: '0.5rem',
                    }}
                  >
                    {negotiation?.occurrenceTypes?.length > 0 ? (
                      negotiation.occurrenceTypes.map(occurrenceType => (
                        <span
                          key={occurrenceType.id}
                          style={{
                            background: '#e9ecef',
                            borderRadius: '12px',
                            padding: '2px 10px',
                            fontSize: '13px',
                          }}
                        >
                          {occurrenceType.name}
                        </span>
                      ))
                    ) : (
                      <Text type="regular" color="gray">
                        Nenhum tipo selecionado
                      </Text>
                    )}
                  </div>
                </Col>
              )}

              <Col xs={12} md={6}>
                <Text type="label" color="dark">
                  Integrações
                </Text>
                <div
                  style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.5rem',
                    marginTop: '0.5rem',
                  }}
                >
                  {negotiation?.integracoes?.length > 0 ? (
                    negotiation.integracoes.map(integracao => (
                      <span
                        key={integracao.id}
                        style={{
                          background: '#e9ecef',
                          borderRadius: '12px',
                          padding: '2px 10px',
                          fontSize: '13px',
                        }}
                      >
                        {integracao.name}
                      </span>
                    ))
                  ) : (
                    <Text type="regular" color="gray">
                      Nenhuma integração
                    </Text>
                  )}
                </div>
              </Col>
            </>
          )}
        </Row>
      </Section>

      <Modal
        show={confirmDeactivateModal.isOpen}
        handleClose={confirmDeactivateModal.close}
        heading="Desativar negociação"
        body={
          <Text type="regular" color="dark">
            Deseja desativar esta negociação?
          </Text>
        }
        footer={
          <Space x="5px">
            <Button
              variant="secondary"
              onClick={confirmDeactivateModal.close}
            >
              <Text type="regular" color="dark" weight="500">
                Cancelar
              </Text>
            </Button>
            <Button variant="primary" onClick={deactivateNegotiation}>
              <Text type="regular" color="white" weight="500">
                Confirmar
              </Text>
            </Button>
          </Space>
        }
      />
    </ApplicationLayout>
  );
}
